import React, { useState, useEffect, useCallback } from 'react';
import { reportService, supermercadoService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function BatchOrderComparisonReport() {
  const [report, setReport] = useState([]);
  const [supermercados, setSupermercados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  
  const [filters, setFilters] = useState({
    supermercadoId: '',
    fromDate: '',
    toDate: '',
    sortBy: 'diferencia_desc',
    sortDirection: 'desc'
  });

  useEffect(() => {
    const fetchSupermercados = async () => {
      try {
        const response = await supermercadoService.getAllSupermercados();
        if (response.success) {
          setSupermercados(response.data || []);
        }
      } catch (err) {
        console.error('Error loading supermercados:', err);
      }
    };
    fetchSupermercados();
  }, []);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      
      const filterParams = {
        ...filters
      };
      
      // Clean empty values
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] === '') {
          delete filterParams[key];
        }
      });
      
      const response = await reportService.getBatchOrderComparisonReport(filterParams);
      if (response.success) {
        setReport(response.data || []);
      } else {
        setError(response.message || 'Error al cargar el reporte');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar el reporte de comparación');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      supermercadoId: '',
      fromDate: '',
      toDate: '',
      sortBy: 'diferencia_desc',
      sortDirection: 'desc'
    });
  };

  const handleSortChange = (sortType) => {
    setFilters(prev => ({ ...prev, sortBy: sortType }));
  };

  return (
    <div className="report-container">
      <h2>Reporte de Comparación: Batch vs Órdenes</h2>
      <p className="report-description">
        Este reporte compara la cantidad de productos dados de baja del stock mediante el batch 
        contra la cantidad que entró al stock en órdenes recibidas por supermercado.
      </p>

      {error && <ErrorDisplay message={error} errorTrace={errorTrace} />}

      <div className="filters-section">
        <div className="filter-group">
          <label>Supermercado:</label>
          <select
            value={filters.supermercadoId}
            onChange={(e) => handleFilterChange('supermercadoId', e.target.value)}
          >
            <option value="">Todos</option>
            {supermercados.map(sup => (
              <option key={sup.id} value={sup.id}>{sup.nombre}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Desde:</label>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Hasta:</label>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Ordenar por:</label>
          <select
            value={filters.sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
          >
            <option value="diferencia_desc">Diferencia (Mayor a Menor)</option>
            <option value="diferencia_asc">Diferencia (Menor a Mayor)</option>
            <option value="product_name">Nombre del Producto</option>
          </select>
        </div>

        <div className="filter-actions">
          <button onClick={handleClearFilters} className="btn-secondary">
            Limpiar Filtros
          </button>
        </div>
      </div>

      {loading ? (
        <p>Cargando reporte...</p>
      ) : (
        <div className="report-results">
          <h3>Resultados ({report.length} productos)</h3>
          
          {report.length === 0 ? (
            <p>No hay datos para mostrar con los filtros seleccionados.</p>
          ) : (
            <table className="report-table">
              <thead>
                <tr>
                  <th>Código de Barras</th>
                  <th>Producto</th>
                  <th>Cantidad Baja (Batch)</th>
                  <th>Cantidad Entrada (Órdenes)</th>
                  <th 
                    onClick={() => handleSortChange(
                      filters.sortBy === 'diferencia_desc' ? 'diferencia_asc' : 'diferencia_desc'
                    )}
                    style={{ cursor: 'pointer' }}
                    title="Click para cambiar orden"
                  >
                    Diferencia {filters.sortBy === 'diferencia_desc' ? '▼' : filters.sortBy === 'diferencia_asc' ? '▲' : ''}
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.map(item => (
                  <tr key={item.productId}>
                    <td>{item.productBarcode}</td>
                    <td>{item.productName}</td>
                    <td className="number-cell">{item.cantidadBaja}</td>
                    <td className="number-cell">{item.cantidadEntrada}</td>
                    <td className={`number-cell ${item.diferencia < 0 ? 'negative' : item.diferencia > 0 ? 'positive' : ''}`}>
                      {item.diferencia}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <style jsx>{`
        .report-container {
          padding: 20px;
        }

        .report-description {
          margin-bottom: 20px;
          color: #666;
          font-style: italic;
        }

        .filters-section {
          background: #f5f5f5;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 20px;
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          align-items: flex-end;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          min-width: 150px;
        }

        .filter-group label {
          margin-bottom: 5px;
          font-weight: 600;
          font-size: 14px;
        }

        .filter-group input,
        .filter-group select {
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .filter-actions {
          display: flex;
          gap: 10px;
          align-items: flex-end;
        }

        .btn-primary,
        .btn-secondary {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: background-color 0.2s;
        }

        .btn-primary {
          background-color: #007bff;
          color: white;
        }

        .btn-primary:hover {
          background-color: #0056b3;
        }

        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background-color: #545b62;
        }

        .report-results {
          margin-top: 20px;
        }

        .report-results h3 {
          margin-bottom: 15px;
        }

        .report-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .report-table th,
        .report-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #ddd;
        }

        .report-table th {
          background-color: #f8f9fa;
          font-weight: 600;
          position: sticky;
          top: 0;
        }

        .report-table tbody tr:hover {
          background-color: #f5f5f5;
        }

        .number-cell {
          text-align: right;
          font-family: monospace;
        }

        .positive {
          color: #28a745;
          font-weight: 600;
        }

        .negative {
          color: #dc3545;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}

export default BatchOrderComparisonReport;
